import {
  ContextualSeatmapData,
  Journey,
  JourneyElementSeatData,
  WarningMessage,
} from '@dapi/sdk';
import {
  createJourneyElementSeatData,
  createPrice,
  createSeatmapSeatProperties,
} from '@dapi/sdk/spec';
import {
  assignSeatToJourneyElements,
  overrideSeatmapAvailability,
  retrieveSeatMapsJourneyDriven,
  SeatAvailabilityOverride,
} from '../../configuration/mocks/common/seatmap';
import {
  journeys,
  multiLegJourneyElementIds,
  multiLegJourneys,
  multiLegTravelerIds,
  roundTripJourneys,
} from './mock-journeys';

export interface SeatmapMockData {
  /** Contextual seatmap data */
  seatmap: ContextualSeatmapData;

  /** First free seat on the journey */
  firstFreeSeatOnJourney: JourneyElementSeatData[];

  /** Second free seat on the journey */
  secondFreeSeatOnJourney: JourneyElementSeatData[];

  /** First Chargeable seat on the journey */
  firstCHSeatOnJourney: JourneyElementSeatData[];

  /** First seat on the journey */
  firstSeatOnJourney: JourneyElementSeatData[];

  /** Second Chargeable seat on the journey */
  secondCHSeatOnJourney: JourneyElementSeatData[];
}

/**
 * Creates the mock data for the seatmap for the given journeys
 *
 * @param journeyList the given journeys
 * @param overrides the list of seat availability overrides
 */
export function createSeatmapMockData(journeyList: Journey[], overrides: SeatAvailabilityOverride[] = []): SeatmapMockData {
  const travelerIdList = journeyList[0].travelers.map((t) => t.id).filter((id): id is string => !!id);
  const contextualSeatmapData = retrieveSeatMapsJourneyDriven(journeyList[0], travelerIdList);
  const seatmapData = overrides.length > 0 ? overrideSeatmapAvailability(contextualSeatmapData, travelerIdList, overrides) : contextualSeatmapData;
  const firstFreeSeatDataOnJourney = assignSeatToJourneyElements(journeyList[0], seatmapData, 0, false);
  const secondFreeSeatDataOnJourney = assignSeatToJourneyElements(journeyList[0], seatmapData, 1, false);
  const firstCHSeatDataOnJourney = assignSeatToJourneyElements(journeyList[0], seatmapData, 0, true);
  const firstSeatDataOnJourney = assignSeatToJourneyElements(journeyList[0], seatmapData, 0);
  const secondCHSeatDataOnJourney = assignSeatToJourneyElements(journeyList[0], seatmapData, 1, true);

  return {
    seatmap: seatmapData,
    firstFreeSeatOnJourney: firstFreeSeatDataOnJourney,
    secondFreeSeatOnJourney: secondFreeSeatDataOnJourney,
    firstCHSeatOnJourney: firstCHSeatDataOnJourney,
    firstSeatOnJourney: firstSeatDataOnJourney,
    secondCHSeatOnJourney: secondCHSeatDataOnJourney
  };
}

export const {seatmap,
  firstFreeSeatOnJourney,
  secondFreeSeatOnJourney,
  firstCHSeatOnJourney,
  firstSeatOnJourney,
  secondCHSeatOnJourney} = createSeatmapMockData(journeys);

export const seatAvailabilityOverrides: SeatAvailabilityOverride[] = [
  {
    seatNumber: '1A', // chargeable seat
    status: 'available'
  },
  {
    seatNumber: '1B', // chargeable seat
    status: 'available'
  },
  {
    seatNumber: '10A', // chargeable exit row seat
    status: 'available',
    isExitRow: true
  },
  {
    seatNumber: '10H', // chargeable exit row seat
    status: 'available',
    isExitRow: true
  },
  {
    seatNumber: '2A', // free seat
    status: 'available'
  },
  {
    seatNumber: '2B', // free seat
    status: 'available'
  },
  {
    seatNumber: '2C', // free seat
    status: 'available'
  },
  {
    seatNumber: '2D', // free seat
    status: 'available'
  },
  {
    seatNumber: '2E', // free seat
    status: 'available'
  }
];

export const {seatmap: owtripSeatmap,
  firstFreeSeatOnJourney: firstFreeSeatOnOwTripJourney,
  secondFreeSeatOnJourney: secondFreeSeatOnOwTripJourney,
  firstCHSeatOnJourney: firstCHSeatOnOwTripJourney,
  firstSeatOnJourney: firstSeatOnOwTripJourney} = createSeatmapMockData(journeys, seatAvailabilityOverrides);

export const {seatmap: multiLegSeatmap,
  firstFreeSeatOnJourney: firstFreeSeatOnMultiLegJourney,
  secondFreeSeatOnJourney: secondFreeSeatOnMultiLegJourney,
  firstCHSeatOnJourney: firstCHSeatOnMultiLegJourney,
  firstSeatOnJourney: firstSeatOnMultiLegJourney} = createSeatmapMockData(multiLegJourneys, seatAvailabilityOverrides);
export const firstExitRowSeatOnMultiLegJourney = {
  ...firstCHSeatOnJourney[0],
  seatNumber: '10A',
  seatCharacteristicsCodes: ['E']
};

export const {seatmap: roundTripSeatmap,
  firstFreeSeatOnJourney: firstFreeSeatOnRoundTripJourney,
  secondFreeSeatOnJourney: secondFreeSeatOnRoundTripJourney,
  firstCHSeatOnJourney: firstCHSeatOnRoundTripJourney,
  firstSeatOnJourney: firstSeatOnRoundTripJourney} = createSeatmapMockData([roundTripJourneys[2]], [
  ...seatAvailabilityOverrides,
  {
    seatNumber: '1B',
    status: 'available'
  },
  {
    seatNumber: '1C',
    status: 'available'
  }
]);

export const firstFreeSeat = createSeatmapSeatProperties({
  template: {
    blockId: '',
    cabin: 'Y',
    isInfantOnSeat: false,
    seatNumber: firstFreeSeatOnJourney[0].seatNumber,
    seatType: '',
    seatAvailabilityStatus: 'occupied'
  }
});

export const secondFreeSeat = createSeatmapSeatProperties({
  template: {
    blockId: '',
    cabin: 'Y',
    isInfantOnSeat: false,
    seatNumber: secondFreeSeatOnJourney[0].seatNumber,
    seatType: '',
    seatAvailabilityStatus: 'occupied'
  }
});

/** Mock response for the update of different free multi-leg seats */
export const multiLegDifferentFreeSeatsUpdateResponse = [
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'NCE',
        offPointIataCode: 'CDG'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'O', 'W'],
      seatNumber: '2B',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available'
      }))
    }
  }),
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'CDG',
        offPointIataCode: 'LHR'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'O', 'W'],
      seatNumber: '2D',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available'
      }))
    }
  })
];

/** Mock response for the update of the same free multi-leg seats */
export const multiLegSameFreeSeatsUpdateResponse = [
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'NCE',
        offPointIataCode: 'CDG'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'O', 'W'],
      seatNumber: '2B',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available'
      }))
    }
  }),
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'CDG',
        offPointIataCode: 'LHR'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'O', 'W'],
      seatNumber: '2B',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available'
      }))
    }
  })
];

/** Mock response for the update of the same chargeable multi-leg seats */
export const multiLegSameChargeableSeatsUpdateResponse = [
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'NCE',
        offPointIataCode: 'CDG'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'CH', 'O', 'W'],
      seatNumber: '1A',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available',
        prices: [createPrice({
          template: {
            currencyCode: 'EUR',
            total: 500
          }
        })]
      }))
    }
  }),
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'CDG',
        offPointIataCode: 'LHR'
      },
      seatAvailabilityStatus: 'available',
      seatCharacteristicsCodes: ['N', 'CH', 'O', 'W'],
      seatNumber: '1A',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'available',
        prices: [createPrice({
          template: {
            currencyCode: 'EUR',
            total: 500
          }
        })]
      }))
    }
  })
];

/** Mock response for the update of the same chargeable multi-leg exit row seats */
export const multiLegExitRowChargeableSeatsUpdateResponse = [
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'NCE',
        offPointIataCode: 'CDG'
      },
      seatAvailabilityStatus: 'occupied',
      seatCharacteristicsCodes: ['N', 'CH', 'O', 'W'],
      seatNumber: '10A',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'occupied',
        prices: [createPrice({
          template: {
            currencyCode: 'EUR',
            total: 500
          }
        })]
      }))
    }
  }),
  createJourneyElementSeatData({
    template: {
      cabin: 'Y',
      journeyElementId: multiLegJourneyElementIds[0],
      leg: {
        boardPointIataCode: 'CDG',
        offPointIataCode: 'LHR'
      },
      seatAvailabilityStatus: 'occupied',
      seatCharacteristicsCodes: ['N', 'CH', 'O', 'W'],
      seatNumber: '10A',
      travelers: multiLegTravelerIds.map((id) => ({
        id,
        seatAvailabilityStatus: 'occupied',
        prices: [createPrice({
          template: {
            currencyCode: 'EUR',
            total: 500
          }
        })]
      }))
    }
  })
];

/**
 * Warning message for exit row eligibility
 */
export const exitRowEligibilityWarning: WarningMessage = {
  id: '1',
  code: '6017',
  title: 'Passenger not eligible for Exit Seat. Seat must be reassigned.',
  type: 'warning'
};

/**
 * Warning message for seat update failure for exit row seat
 */
export const exitRowSeatUpdateWarning: WarningMessage = {
  id: '2',
  code: '6016',
  title: 'Seat change failed. The target seat is not available to the customer',
  type: 'warning'
};
